// Cosmetics: ownership, purchases (tokens / cash / achievement unlocks), equip per rig, company defaults, resolver.
// Cosmetic only — nothing here changes the simulation. Client-side for now; every purchase writes a receipt-ready
// record (idempotency key = one per item, since an item can only be owned once) so a payments server can reconcile
// it later (MONETIZATION §6). State (JSON-safe):
//   state.tokens.cosmetics: [itemId]                         — owned items (purchased or granted)
//   state.cosmetics = { equipped: { [rigUid]: { skin?, uniform? } },   — per-rig overrides ('none' = factory look)
//                       defaults: { skin: { [family]: id }, uniform: id|null },
//                       purchases: [{ idemKey, sku, item, price, currency, day, at, status }] }
import { COSMETICS, BUNDLE_BY_ID, BUNDLES, SKINS, UNIFORMS, BUNDLE_DISCOUNT, FAMILIES, skinFits, rigFamily, rigKindOf, familyOfKind, listPrice, skinLook, uniformLook, UNIFORM_STYLES } from '../data/cosmetics.js';
import { inflate } from '../data/cpi.js';
import { dayToDate } from '../core/time.js';
import { spend as spendTokens, balance } from './tokens.js';
import { spend as spendCash } from '../sim/util.js';

export const CONFIRM_ABOVE = 50; // tokens: UI asks for confirmation above this (MONETIZATION §1.3)
const PURCHASE_CAP = 500;
const no = (msg) => ({ ok: false, msg });
const yearOf = (state) => dayToDate(state.day ?? 0).year;

export function newCosmetics() { return { equipped: {}, defaults: { skin: {}, uniform: null }, purchases: [] }; }

// Lazy init + migration of older saves (missing keys → defaults; drops references to unknown items).
export function ensureCosmetics(state) {
  state.tokens ??= {};
  if (!Array.isArray(state.tokens.cosmetics)) state.tokens.cosmetics = [];
  const c = state.cosmetics && typeof state.cosmetics === 'object' ? state.cosmetics : (state.cosmetics = newCosmetics());
  c.equipped = c.equipped && typeof c.equipped === 'object' ? c.equipped : {};
  c.defaults = c.defaults && typeof c.defaults === 'object' ? c.defaults : {};
  c.defaults.skin = c.defaults.skin && typeof c.defaults.skin === 'object' ? c.defaults.skin : {};
  if (c.defaults.uniform !== null && !COSMETICS[c.defaults.uniform]) c.defaults.uniform = null;
  for (const f of Object.keys(c.defaults.skin)) if (!COSMETICS[c.defaults.skin[f]]) delete c.defaults.skin[f];
  if (!Array.isArray(c.purchases)) c.purchases = [];
  return c;
}
// Save migration: sanitize when present; a save without `state.cosmetics` keeps working (every reader treats it as
// defaults and the first write creates it), so migrating a fresh game stays an identity.
export const migrateCosmetics = (state) => {
  if (state.tokens && !Array.isArray(state.tokens.cosmetics)) state.tokens.cosmetics = [];
  if (state.cosmetics != null) ensureCosmetics(state);
  return state;
};

// ------------------------------------------------------------------------------------------ ownership
function unlockMet(state, unlock) {
  if (!unlock) return false;
  const ach = state.achievements ?? {};
  if (unlock.achievement) return ach[unlock.achievement] != null;
  if (unlock.achievements) return Object.keys(ach).length >= unlock.achievements;
  return false;
}
export function unlockText(unlock) {
  if (!unlock) return '';
  if (unlock.achievement) return `Achievement: ${unlock.achievement.replace(/_/g, ' ')}`;
  if (unlock.achievements) return `Earn ${unlock.achievements} achievements`;
  return '';
}
// Owned = bought/granted, or an achievement unlock whose condition is met (derived, so it can never be lost).
export function owns(state, id) {
  if (state.tokens?.cosmetics?.includes(id)) return true;
  const d = COSMETICS[id];
  return !!(d?.price?.unlock && unlockMet(state, d.price.unlock));
}
export const ownedIds = (state) => Object.keys(COSMETICS).filter((id) => owns(state, id));

export function priceOf(state, id) {
  const d = COSMETICS[id];
  if (!d) return null;
  return listPrice(d, { inflate, year: yearOf(state) });
}

export function bundlePrice(state, bundleId) {
  const b = BUNDLE_BY_ID[bundleId];
  if (!b) return null;
  const items = b.items.map((id) => COSMETICS[id]).filter(Boolean);
  const list = items.reduce((a, d) => a + (d.price?.tokens ?? 0), 0);
  const missing = items.filter((d) => !owns(state, d.id));
  const due = missing.reduce((a, d) => a + (d.price?.tokens ?? 0), 0);
  const price = due > 0 ? Math.max(10, Math.floor((due * BUNDLE_DISCOUNT) / 10) * 10) : 0;
  return { currency: 'tokens', amount: price, list, due, saving: due - price, missing: missing.map((d) => d.id), complete: missing.length === 0 };
}

export function canBuy(state, id) {
  const d = COSMETICS[id];
  if (!d) return no('Unknown item.');
  if (owns(state, id)) return no(`You already own ${d.name}.`);
  const y = yearOf(state);
  if (d.fromYear && y < d.fromYear) return no(`${d.name} is available from ${d.fromYear}.`);
  const p = priceOf(state, id);
  if (p.currency === 'unlock') return no(`${d.name} unlocks for free: ${unlockText(p.unlock)}.`);
  if (p.currency === 'tokens' && balance(state) < p.amount) return { ...no(`Need ${p.amount} ◈ (you have ${balance(state)}).`), price: p };
  if (p.currency === 'cash' && (state.company?.cash ?? 0) < p.amount) return { ...no(`Need $${p.amount.toLocaleString('en-US')} cash.`), price: p };
  return { ok: true, msg: '', price: p };
}

function record(state, { sku, item, price, currency, status = 'local' }) {
  const c = ensureCosmetics(state);
  const idemKey = `cos:${sku}`;
  let at = null; try { at = Date.now(); } catch { /* ignore */ }
  c.purchases.push({ idemKey, sku, item, price, currency, day: state.day ?? null, at, status });
  if (c.purchases.length > PURCHASE_CAP) c.purchases.splice(0, c.purchases.length - PURCHASE_CAP);
  if (currency === 'tokens' && price > 0) {
    const w = state.tokens;
    if (Array.isArray(w.pendingSpends)) w.pendingSpends.push({ idemKey, sku: `cosmetic:${sku}`, amount: price, ref: item, createdAt: at, local: true });
  }
}
function addOwned(state, id) {
  ensureCosmetics(state);
  if (!state.tokens.cosmetics.includes(id)) state.tokens.cosmetics.push(id);
}

export function buyCosmetic(state, id) {
  ensureCosmetics(state);
  const chk = canBuy(state, id);
  if (!chk.ok) return chk;
  const d = COSMETICS[id], p = chk.price;
  if (p.currency === 'tokens') {
    const r = spendTokens(state, p.amount, 'cosmetic', id);
    if (!r.ok) return r;
  } else if (p.currency === 'cash') {
    if (!spendCash(state, p.amount, 'other')) return no('Not enough cash.');
  }
  addOwned(state, id);
  record(state, { sku: id, item: id, price: p.amount, currency: p.currency });
  return { ok: true, good: true, msg: `Bought ${d.name}${p.currency === 'tokens' ? ` for ${p.amount} ◈` : p.currency === 'cash' ? ` for $${p.amount.toLocaleString('en-US')}` : ''}.`, id };
}

export function buyCosmeticBundle(state, bundleId) {
  ensureCosmetics(state);
  const b = BUNDLE_BY_ID[bundleId];
  if (!b) return no('Unknown bundle.');
  const y = yearOf(state);
  const late = b.items.map((id) => COSMETICS[id]).find((d) => d && d.fromYear > y && !owns(state, d.id));
  if (late) return no(`${b.name} includes ${late.name}, available from ${late.fromYear}.`);
  const p = bundlePrice(state, bundleId);
  if (p.complete) return no(`You already own everything in ${b.name}.`);
  const r = spendTokens(state, p.amount, 'cosmetic', bundleId);
  if (!r.ok) return r;
  for (const id of p.missing) addOwned(state, id);
  record(state, { sku: `bundle:${bundleId}:${p.missing.join('+')}`, item: bundleId, price: p.amount, currency: 'tokens' });
  return { ok: true, good: true, msg: `Bought ${b.name} for ${p.amount} ◈ (saved ${p.saving} ◈).`, ids: p.missing };
}

// Server receipts / Founder's Bundle / promo grants. Idempotent.
export function grantCosmetic(state, id, reason = 'grant') {
  if (!COSMETICS[id]) return no('Unknown item.');
  if (state.tokens?.cosmetics?.includes(id)) return { ok: true, msg: 'Already owned.' };
  addOwned(state, id);
  record(state, { sku: `${reason}:${id}`, item: id, price: 0, currency: 'grant', status: reason });
  return { ok: true, msg: `${COSMETICS[id].name} added to your wardrobe.` };
}

// ------------------------------------------------------------------------------------------ equipping
const rigOf = (state, uid) => state.rigs?.[uid] ?? null;
const rigFam = (rig) => rigFamily(rig?.defId, null, rig?.body);

export function equipSkin(state, rigUid, id) {
  const c = ensureCosmetics(state);
  const rig = rigOf(state, rigUid);
  if (!rig) return no('No such rig.');
  if (id == null) { if (c.equipped[rigUid]) { delete c.equipped[rigUid].skin; tidy(c, rigUid); } return { ok: true, msg: `${rig.name ?? 'Rig'} uses the company default paint.` }; }
  if (id === 'none') { (c.equipped[rigUid] ??= {}).skin = 'none'; return { ok: true, msg: `${rig.name ?? 'Rig'} back to factory paint.` }; }
  const d = COSMETICS[id];
  if (!d || d.kind !== 'skin') return no('Not a rig skin.');
  if (!owns(state, id)) return no(`You don’t own ${d.name} yet.`);
  const kind = rigKindOf(rig.defId, null, rig.body);
  if (!skinFits(d, kind)) return no(`${d.name} doesn’t fit a ${FAMILIES[rigFam(rig)]?.name.toLowerCase() ?? 'rig like this'}.`);
  (c.equipped[rigUid] ??= {}).skin = id;
  return { ok: true, msg: `${d.name} applied to ${rig.name ?? 'the rig'}.` };
}

// rigUid null → company default (same as setDefaultUniform).
export function equipUniform(state, rigUid, id) {
  const c = ensureCosmetics(state);
  if (rigUid == null) return setDefaultUniform(state, id);
  const rig = rigOf(state, rigUid);
  if (!rig) return no('No such rig.');
  if (id == null) { if (c.equipped[rigUid]) { delete c.equipped[rigUid].uniform; tidy(c, rigUid); } return { ok: true, msg: `${rig.name ?? 'Rig'} crew wear the company uniform.` }; }
  if (id === 'none') { (c.equipped[rigUid] ??= {}).uniform = 'none'; return { ok: true, msg: `${rig.name ?? 'Rig'} crew wear their own clothes.` }; }
  const d = COSMETICS[id];
  if (!d || d.kind !== 'uniform') return no('Not a uniform.');
  if (!owns(state, id)) return no(`You don’t own ${d.name} yet.`);
  (c.equipped[rigUid] ??= {}).uniform = id;
  return { ok: true, msg: `${rig.name ?? 'Rig'} crew now wear ${d.name}.` };
}

export function setDefaultSkin(state, family, id) {
  const c = ensureCosmetics(state);
  if (!FAMILIES[family]) return no('Unknown rig family.');
  if (id == null) { delete c.defaults.skin[family]; return { ok: true, msg: `${FAMILIES[family].name}: factory paint by default.` }; }
  const d = COSMETICS[id];
  if (!d || d.kind !== 'skin') return no('Not a rig skin.');
  if (!owns(state, id)) return no(`You don’t own ${d.name} yet.`);
  if (!d.families.includes(family)) return no(`${d.name} doesn’t fit ${FAMILIES[family].name.toLowerCase()}.`);
  c.defaults.skin[family] = id;
  return { ok: true, msg: `${d.name} is now the company default for ${FAMILIES[family].name.toLowerCase()}.` };
}

export function setDefaultUniform(state, id) {
  const c = ensureCosmetics(state);
  if (id == null) { c.defaults.uniform = null; return { ok: true, msg: 'Crews wear their own clothes.' }; }
  const d = COSMETICS[id];
  if (!d || d.kind !== 'uniform') return no('Not a uniform.');
  if (!owns(state, id)) return no(`You don’t own ${d.name} yet.`);
  c.defaults.uniform = id;
  return { ok: true, msg: `${d.name} is now the company uniform.` };
}

function tidy(c, rigUid) { if (c.equipped[rigUid] && !Object.keys(c.equipped[rigUid]).length) delete c.equipped[rigUid]; }

// -------------------------------------------------------------------------------------------- resolver
// What a rig wears right now: override → family default → nothing. Pure (no state writes). opts.defId/kind
// let the renderer resolve for a rig-less view (producing well with the derrick left standing).
export function activeCosmetics(state, rigUid, opts = {}) {
  if (!state) return { skin: null, uniform: null };
  const c = state.cosmetics ?? {};
  const rig = rigUid ? state.rigs?.[rigUid] : null;
  const kind = opts.kind ?? rigKindOf(opts.defId ?? rig?.defId, opts.rigType, opts.body);
  const family = familyOfKind(kind);
  const eq = (rigUid && c.equipped?.[rigUid]) || {};
  let skinId = eq.skin ?? c.defaults?.skin?.[family] ?? null;
  if (skinId === 'none') skinId = null;
  let skin = skinId ? COSMETICS[skinId] : null;
  if (skin && (!owns(state, skin.id) || !skinFits(skin, kind))) skin = null;
  let uniId = eq.uniform ?? c.defaults?.uniform ?? null;
  if (uniId === 'none') uniId = null;
  let uniform = uniId ? COSMETICS[uniId] : null;
  if (uniform && !owns(state, uniform.id)) uniform = null;
  return { skin, uniform, family, kind };
}

// Resolved looks for the renderers: { skin: skinLook|null, uniform: uniformLook|null, key } (key changes when the look does).
export function cosmeticLooks(state, rigUid, opts = {}) {
  const a = activeCosmetics(state, rigUid, opts);
  const logo = state?.company?.logo ?? null;
  const year = opts.year ?? yearOf(state ?? { day: 0 });
  const skin = a.skin ? skinLook(a.skin, { logo }) : null;
  const uniform = a.uniform ? uniformLook(a.uniform, { year, offshore: opts.offshore, body: opts.body, logo }) : null;
  const lk = logo ? `${logo.motif}.${logo.shape}.${logo.scheme}` : '-';
  const skinKey = skin ? `${skin.id}:${skin.decals.length ? lk : ''}` : '-';
  const uniformKey = uniform ? `${uniform.id}:${uniform.style}:${uniform.patch ? lk : ''}` : '-';
  return { skin, uniform, skinKey, uniformKey, key: `${skinKey}/${uniformKey}` };
}

// ---------------------------------------------------------------------------------------------- view
export function cosmeticsView(state) {
  const c = state.cosmetics ?? newCosmetics();
  const y = yearOf(state);
  const item = (d) => {
    const p = priceOf(state, d.id);
    const own = owns(state, d.id);
    const chk = own ? null : canBuy(state, d.id);
    return {
      id: d.id, kind: d.kind, name: d.name, blurb: d.blurb, tags: d.tags ?? [], fromYear: d.fromYear ?? 1859, families: d.families ?? null, kinds: d.kinds ?? null,
      style: d.style ?? null, styleName: d.style ? (d.style === 'era' ? 'Adapts to the era' : UNIFORM_STYLES[d.style]?.name) : null,
      price: p, owned: own, available: y >= (d.fromYear ?? 0), canBuy: !!chk?.ok, reason: chk && !chk.ok ? chk.msg : '',
      unlock: p.unlock ? unlockText(p.unlock) : null, confirm: p.currency === 'tokens' && p.amount > CONFIRM_ABOVE,
      defaultFor: d.kind === 'skin' ? Object.entries(c.defaults?.skin ?? {}).filter(([, v]) => v === d.id).map(([f]) => f) : (c.defaults?.uniform === d.id ? ['company'] : []),
      onRigs: Object.entries(c.equipped ?? {}).filter(([, e]) => e[d.kind] === d.id).map(([uid]) => uid),
    };
  };
  const rigs = Object.values(state.rigs ?? {}).map((r) => {
    const a = activeCosmetics(state, r.uid);
    return { uid: r.uid, name: r.name, defId: r.defId, family: a.family, kind: a.kind, skin: a.skin?.id ?? null, uniform: a.uniform?.id ?? null,
      override: { ...(c.equipped?.[r.uid] ?? {}) }, wellId: r.wellId ?? null };
  });
  return {
    year: y, balance: balance(state), cash: state.company?.cash ?? 0, confirmAbove: CONFIRM_ABOVE,
    skins: SKINS.map(item), uniforms: UNIFORMS.map(item),
    bundles: BUNDLES.map((b) => ({ ...b, price: bundlePrice(state, b.id), available: b.items.every((id) => (COSMETICS[id]?.fromYear ?? 0) <= y || owns(state, id)) })),
    defaults: { skin: { ...(c.defaults?.skin ?? {}) }, uniform: c.defaults?.uniform ?? null },
    rigs, owned: ownedIds(state), purchases: [...(c.purchases ?? [])].reverse().slice(0, 30), families: FAMILIES,
  };
}
